import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  StyleSheet,
  View,
  Modal,
  TouchableOpacity,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Platform,
  Alert,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedInput } from "../ThemedInput";
import { ThemedButton } from "../ThemedButton";
import { ThemedTag } from "./ThemedTag";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useFirebase } from "@components/firebase/useFirebase";
import {
  ITag,
  ITagAssociationMeta,
  TagType,
  TagTargetType,
} from "common247";
import {
  getTags,
  createTag,
  addTagToEntity,
  editTag,
} from "@/services/firebase/tag-functions";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import {
  calculatePopoverPosition,
  type IPopoverAnchor,
  type IPopoverLayout,
} from "@/utils/popover-positioning";

export interface ITagSelectorProps {
  visible: boolean;
  onClose: () => void;
  entityId?: string;
  entityIds?: string[];
  entityType?: TagTargetType;
  currentTags?: Record<string, ITagAssociationMeta>;
  onTagAdded?: (association: ITagAssociationMeta) => void;
  onTagsBatchAdded?: (tagId: string) => void;
  /**
   * Optional contextual anchor position (e.g. cursor or trigger coordinates).
   */
  anchorPosition?: IPopoverAnchor | null;
  /**
   * Whether multiple tags can be selected in a batch.
   */
  multiSelect?: boolean;
  /**
   * Currently or initially selected tag IDs (for multi-select).
   */
  selectedTagIds?: string[];
  /**
   * Callback fired when multiple tags are selected.
   */
  onSelectMultipleTags?: (tags: ITag[]) => void;
  /**
   * Optional title override.
   */
  title?: string;
  /**
   * Optional subtitle or instructions.
   */
  subtitle?: string;
  /**
   * Optional testID.
   */
  testID?: string;
}

const DEFAULT_EMPTY_IDS: string[] = [];

/**
 * Modal for searching, creating, and attaching tags to an entity or batch of entities.
 * Supports both single-tag attachment and multi-tag selection with checkmarks.
 */
export function TagSelector({
  visible,
  onClose,
  entityId,
  entityIds,
  entityType = "memory",
  currentTags = {},
  onTagAdded,
  onTagsBatchAdded,
  anchorPosition,
  multiSelect = false,
  selectedTagIds = DEFAULT_EMPTY_IDS,
  onSelectMultipleTags,
  title,
  subtitle,
  testID = "tag-selector",
}: ITagSelectorProps) {
  const services = useFirebase();
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedTagIds ?? []);
  const [searchQuery, setSearchQuery] = useState("");
  const [platformWide, setPlatformWide] = useState(false);
  const [tags, setTags] = useState<ITag[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [editingTag, setEditingTag] = useState<ITag | null>(null);
  const [editValue, setEditValue] = useState("");

  const [layout, setLayout] = useState<IPopoverLayout>(() =>
    calculatePopoverPosition({
      anchorPosition,
      popoverWidth: 400,
      popoverHeight: 460,
    })
  );

  useEffect(() => {
    if (!visible) return;
    const updateLayout = () => {
      setLayout(
        calculatePopoverPosition({
          anchorPosition,
          popoverWidth: 400,
          popoverHeight: 460,
        })
      );
    };
    updateLayout();
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.addEventListener("resize", updateLayout);
      return () => {
        window.removeEventListener("resize", updateLayout);
      };
    }
  }, [visible, anchorPosition]);

  const { selector: selectorTheme, palette, isDark } = useComponentTheme();
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = selectorTheme.row.selectedBorderColor;
  const badgeBg = selectorTheme.row.restingBg;
  const textMuted = selectorTheme.row.subtitleColor;

  // Fetch available tags
  const fetchTags = useCallback(async () => {
    if (!services || !visible) return;
    setLoading(true);
    try {
      const res = await getTags(services, {
        search: searchQuery.trim() || undefined,
        tagType: "general",
        scope: platformWide ? "all" : "user",
      });

      const tagsList = res.tags;
      if (res.status && tagsList) {
        setTags(tagsList);
      }
    } catch (err) {
      console.warn("[TagSelectorModal] Failed to fetch tags:", err);
    } finally {
      setLoading(false);
    }
  }, [services, visible, searchQuery, platformWide]);

  useEffect(() => {
    if (selectedTagIds) {
      setSelectedIds((prev) => {
        if (
          prev.length === selectedTagIds.length &&
          prev.every((id, idx) => id === selectedTagIds[idx])
        ) {
          return prev;
        }
        return selectedTagIds;
      });
    }
  }, [selectedTagIds]);

  useEffect(() => {
    if (visible) {
      fetchTags();
    } else {
      setSearchQuery("");
      setEditingTag(null);
    }
  }, [visible, fetchTags]);

  const attachedTagIds = useMemo(() => {
    return new Set(Object.keys(currentTags || {}));
  }, [currentTags]);

  const filteredTags = useMemo(() => {
    return tags.filter((t) => {
      if (!searchQuery.trim()) return true;
      return t.value.toLowerCase().includes(searchQuery.trim().toLowerCase());
    });
  }, [tags, searchQuery]);

  const targetIds = useMemo(() => {
    if (entityIds && entityIds.length > 0) return entityIds;
    if (entityId) return [entityId];
    return [];
  }, [entityId, entityIds]);

  const exactGeneralTagExists = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return tags.some((t) => t.value.toLowerCase() === q);
  }, [tags, searchQuery]);

  const handleTagPress = (tag: ITag) => {
    if (multiSelect) {
      const next = selectedIds.includes(tag.id)
        ? selectedIds.filter((id) => id !== tag.id)
        : [...selectedIds, tag.id];
      setSelectedIds(next);
      if (onSelectMultipleTags) {
        const selectedList = tags.filter((t) => next.includes(t.id));
        onSelectMultipleTags(selectedList);
      }
      return;
    }
    handleSelectTag(tag);
  };

  const handleSelectTag = async (tag: ITag) => {
    if (!services || actionInProgress) return;
    if (attachedTagIds.has(tag.id)) {
      return;
    }

    if (targetIds.length === 0) {
      // Creation mode: item has not been persisted to Firestore yet
      const addedAssoc: ITagAssociationMeta = {
        tagId: tag.id,
        addedAt: Date.now(),
        addedBy: services.auth?.currentUser?.uid || "",
      };
      onTagAdded?.(addedAssoc);
      onTagsBatchAdded?.(tag.id);
      onClose();
      return;
    }

    setActionInProgress(true);
    try {
      const results = await Promise.all(
        targetIds.map((tid) =>
          addTagToEntity(services, {
            targetType: entityType,
            targetId: tid,
            tagId: tag.id,
          })
        )
      );

      const successful = results.filter((r) => r.status);
      if (successful.length > 0) {
        const addedAssoc =
          successful[0].tag || successful[0].association || successful[0].data?.association;
        if (addedAssoc) {
          onTagAdded?.(addedAssoc);
        }
        onTagsBatchAdded?.(tag.id);
        onClose();
      } else {
        const msg = results[0]?.error || "Failed to add tag to items.";
        if (Platform.OS === "web") {
          window.alert(msg);
        } else {
          Alert.alert("Error", msg);
        }
      }
    } catch (err: any) {
      console.error("[TagSelectorModal] Error adding tag:", err);
      const msg = err.message || "An unexpected error occurred.";
      if (Platform.OS === "web") {
        window.alert(msg);
      } else {
        Alert.alert("Error", msg);
      }
    } finally {
      setActionInProgress(false);
    }
  };

  const handleCreateAndAttachGeneralTag = async () => {
    const query = searchQuery.trim();
    if (!query || !services || actionInProgress) return;

    setActionInProgress(true);
    try {
      // 1. Create the general tag in global tags collection
      const createRes = await createTag(services, {
        tagType: "general",
        value: query,
      });

      const newTag = createRes.tag;
      if (!createRes.status || !newTag) {
        throw new Error(createRes.error || "Failed to create tag.");
      }

      if (targetIds.length === 0) {
        // Creation mode: item has not been persisted to Firestore yet
        const attachAssoc: ITagAssociationMeta = {
          tagId: newTag.id,
          addedAt: Date.now(),
          addedBy: services.auth?.currentUser?.uid || "",
        };
        onTagAdded?.(attachAssoc);
        onTagsBatchAdded?.(newTag.id);
        onClose();
        return;
      }

      // 2. Associate with target entities
      const results = await Promise.all(
        targetIds.map((tid) =>
          addTagToEntity(services, {
            targetType: entityType,
            targetId: tid,
            tagId: newTag.id,
          })
        )
      );

      const successful = results.filter((r) => r.status);
      if (successful.length > 0) {
        const attachAssoc =
          successful[0].tag || successful[0].association;
        if (attachAssoc) {
          onTagAdded?.(attachAssoc);
        }
        onTagsBatchAdded?.(newTag.id);
        onClose();
      } else {
        throw new Error(results[0]?.error || "Tag created but failed to attach to items.");
      }
    } catch (err: any) {
      console.error("[TagSelectorModal] Error creating tag:", err);
      const msg = err.message || "Failed to create and add tag.";
      if (Platform.OS === "web") {
        window.alert(msg);
      } else {
        Alert.alert("Error", msg);
      }
    } finally {
      setActionInProgress(false);
    }
  };

  const handleStartEditTag = (tag: ITag) => {
    setEditingTag(tag);
    setEditValue(tag.value);
  };

  const handleConfirmEditTag = async () => {
    if (!services || !editingTag || !editValue.trim() || actionInProgress) return;

    const performUpdate = async () => {
      setActionInProgress(true);
      try {
        const res = await editTag(services, {
          tagId: editingTag.id,
          value: editValue.trim(),
        });
        if (res.status && (res.tag || res.data)) {
          setEditingTag(null);
          fetchTags();
        } else {
          throw new Error(res.error || "Failed to update tag.");
        }
      } catch (err: any) {
        const msg = err.message || "Failed to update tag.";
        if (Platform.OS === "web") {
          window.alert(msg);
        } else {
          Alert.alert("Error", msg);
        }
      } finally {
        setActionInProgress(false);
      }
    };

    const warningMsg =
      "Warning: Updating this tag will update all items that have this tag across the platform. Do you want to proceed?";

    if (Platform.OS === "web") {
      if (window.confirm(warningMsg)) {
        await performUpdate();
      }
    } else {
      Alert.alert(
        "Update Tag Everywhere?",
        warningMsg,
        [
          { text: "Cancel", style: "cancel" },
          { text: "Update All Items", style: "destructive", onPress: performUpdate },
        ]
      );
    }
  };

  if (!visible) return null;

  const modalTitle =
    title ??
    (multiSelect
      ? "Select Tags"
      : targetIds.length > 1
      ? `Add Tag to ${targetIds.length} ${entityType === "media" ? "Media Items" : entityType + "s"}`
      : `Add Tag to ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`);

  const modalSubtitle =
    subtitle ??
    (multiSelect
      ? `${selectedIds.length} tag${selectedIds.length === 1 ? "" : "s"} selected`
      : undefined);

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={modalTitle}
      subtitle={modalSubtitle}
      icon="pricetag-outline"
      width={layout.width}
      maxHeight={layout.maxHeight}
      cardStyle={{ backgroundColor: cardBg, borderColor }}
      testID={testID}
      footer={
        multiSelect ? (
          <View style={styles.footerRow}>
            <ThemedButton
              title="Cancel"
              variant="secondary"
              onPress={onClose}
              style={styles.cancelBtn}
            />
            <ThemedButton
              title="Done"
              variant="primary"
              compact
              onPress={() => {
                if (onSelectMultipleTags) {
                  const selectedList = tags.filter((t) => selectedIds.includes(t.id));
                  onSelectMultipleTags(selectedList);
                }
                onClose();
              }}
              style={styles.doneBtn}
              testID={`${testID}-done-btn`}
            />
          </View>
        ) : undefined
      }
    >
      <View style={{ flex: 1 }}>

          {/* Edit Tag Sub-Panel */}
          {editingTag ? (
            <View style={[styles.editPanel, { backgroundColor: badgeBg }]}>
              <ThemedText type="defaultSemiBold" style={styles.editPanelTitle}>
                Edit Tag Value:
              </ThemedText>
              <ThemedInput
                value={editValue}
                onChangeText={setEditValue}
                placeholder="New tag name"
                style={styles.editInput}
              />
              <ThemedText style={styles.editWarningText}>
                ⚠️ Warning: Changing this tag updates all items tagged with it across the platform.
              </ThemedText>
              <View style={styles.editActionsRow}>
                <TouchableOpacity
                  onPress={() => setEditingTag(null)}
                  style={[styles.smallBtn, { borderColor }]}
                >
                  <ThemedText style={styles.smallBtnText}>Cancel</ThemedText>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleConfirmEditTag}
                  disabled={actionInProgress || !editValue.trim()}
                  style={[styles.smallBtn, { backgroundColor: tintColor }]}
                >
                  <ThemedText style={[styles.smallBtnText, { color: "#FFFFFF" }]}>
                    Save Everywhere
                  </ThemedText>
                </TouchableOpacity>
              </View>
            </View>
          ) : null}

          {/* Search bar */}
          <View style={styles.searchSection}>
            <ThemedInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="Search tags or type new tag..."
              style={styles.searchInput}
            />
            <View style={styles.scopeRow}>
              <TouchableOpacity
                onPress={() => setPlatformWide(false)}
                style={[
                  styles.scopeChip,
                  !platformWide && { backgroundColor: tintColor },
                  { borderColor },
                ]}
              >
                <ThemedText
                  style={[
                    styles.scopeChipText,
                    !platformWide && { color: "#FFFFFF", fontWeight: "700" },
                  ]}
                >
                  My & Visible Tags
                </ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setPlatformWide(true)}
                style={[
                  styles.scopeChip,
                  platformWide && { backgroundColor: tintColor },
                  { borderColor },
                ]}
              >
                <ThemedText
                  style={[
                    styles.scopeChipText,
                    platformWide && { color: "#FFFFFF", fontWeight: "700" },
                  ]}
                >
                  Platform-Wide
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>

          {/* Tags list & Quick Add */}
          <ScrollView style={styles.tagsContainer}>
            {loading ? (
              <View style={styles.centerSpinner}>
                <ActivityIndicator size="small" color={tintColor} />
              </View>
            ) : (
              <View style={styles.tagsFlexWrap}>
                {/* Create Tag Option */}
                {searchQuery.trim().length > 0 && !exactGeneralTagExists && (
                  <TouchableOpacity
                    onPress={handleCreateAndAttachGeneralTag}
                    disabled={actionInProgress}
                    style={[
                      styles.createTagCard,
                      { borderColor: selectorTheme.createRow.borderColor, backgroundColor: selectorTheme.createRow.backgroundColor },
                    ]}
                  >
                    <Ionicons name="add-circle-outline" size={18} color={tintColor} />
                    <ThemedText style={[styles.createTagText, { color: tintColor }]}>
                      Create Tag: <ThemedText type="defaultSemiBold">"{searchQuery.trim()}"</ThemedText>
                    </ThemedText>
                  </TouchableOpacity>
                )}

                {filteredTags.map((t) => {
                  const isAttached = attachedTagIds.has(t.id);
                  const isSelected = selectedIds.includes(t.id);
                  return (
                    <View key={t.id} style={styles.tagItemRow}>
                      <ThemedTag
                        tag={t}
                        readOnly={isAttached}
                        onPress={() => !isAttached && handleTagPress(t)}
                        style={[
                          isAttached ? { opacity: 0.5 } : undefined,
                          multiSelect && isSelected ? { borderWidth: 2, borderColor: tintColor } : undefined,
                        ]}
                      />
                      {isAttached ? (
                        <ThemedText style={[styles.attachedBadge, { color: textMuted }]}>
                          ✓ Added
                        </ThemedText>
                      ) : multiSelect ? (
                        <TouchableOpacity
                          onPress={() => handleTagPress(t)}
                          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                          style={styles.checkIconBtn}
                        >
                          <Ionicons
                            name={isSelected ? "checkmark-circle" : "ellipse-outline"}
                            size={16}
                            color={isSelected ? tintColor : textMuted}
                          />
                        </TouchableOpacity>
                      ) : (
                        <TouchableOpacity
                          onPress={() => handleStartEditTag(t)}
                          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                          style={styles.editIconBtn}
                        >
                          <Ionicons name="pencil-outline" size={13} color={textMuted} />
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                })}

                {filteredTags.length === 0 && !searchQuery.trim() && (
                  <View style={styles.emptyNotice}>
                    <ThemedText style={{ color: textMuted }}>
                      No tags found. Type a tag name to create one.
                    </ThemedText>
                  </View>
                )}
              </View>
            )}
          </ScrollView>
        </View>
    </ThemedPopupScreen>
  );
}

/**
 * Backward compatibility alias for TagSelector.
 */
export const TagSelectorModal = TagSelector;

const styles = StyleSheet.create({
  hoverCard: {
    position: "absolute",
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 12,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerIcon: {
    marginRight: 8,
  },
  title: {
    fontSize: 16,
  },
  closeBtn: {
    padding: 4,
  },
  searchSection: {
    padding: 8,
  },
  searchInput: {
    marginBottom: 6,
  },
  scopeRow: {
    flexDirection: "row",
    gap: 6,
  },
  scopeChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    borderWidth: 1,
  },
  scopeChipText: {
    fontSize: 11,
  },
  tagsContainer: {
    padding: 8,
    maxHeight: 250,
    flexShrink: 1,
  },
  tagsFlexWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  centerSpinner: {
    padding: 20,
    alignItems: "center",
  },
  createTagCard: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderStyle: "dashed",
    gap: 6,
    marginBottom: 6,
  },
  createTagText: {
    fontSize: 12,
  },
  tagItemRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  attachedBadge: {
    fontSize: 10,
    marginLeft: 2,
    marginRight: 6,
  },
  editIconBtn: {
    marginLeft: -2,
    marginRight: 6,
    padding: 2,
  },
  emptyNotice: {
    width: "100%",
    padding: 20,
    alignItems: "center",
  },
  editPanel: {
    padding: 8,
    margin: 8,
    borderRadius: 6,
  },
  editPanelTitle: {
    fontSize: 12,
    marginBottom: 4,
  },
  editInput: {
    marginBottom: 4,
  },
  editWarningText: {
    fontSize: 10,
    color: "#E11D48",
    marginBottom: 6,
  },
  editActionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 6,
  },
  smallBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
  },
  smallBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 6,
    gap: 6,
    borderTopWidth: 1,
  },
  cancelBtn: {
    minWidth: 64,
  },
  doneBtn: {
    minWidth: 75,
  },
  checkIconBtn: {
    padding: 3,
  },
});

export default TagSelector;

